import { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, X, Loader2, ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { uploadClinicSiteImage, resolveMediaUrl } from '../lib/api';

/** @typedef {'logo' | 'banner' | 'hero'} PreviewVariant */

/**
 * Campo URL + subida local con vista previa.
 */
export default function SiteImageField({
  label = 'Imagen',
  url,
  onChange,
  inputId: inputIdProp,
  previewVariant = 'banner',
  uploadFile = uploadClinicSiteImage,
}) {
  const reactId = useId();
  const inputId = inputIdProp ?? `site-img-${reactId}`;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const blobRef = useRef(null);
  const [localPreview, setLocalPreview] = useState(null);

  const revokeBlob = () => {
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }
  };

  useEffect(() => () => revokeBlob(), []);

  const displaySrc =
    localPreview || (typeof url === 'string' && url.trim() !== '' ? resolveMediaUrl(url) : '');

  const previewWrap =
    previewVariant === 'logo'
      ? 'flex justify-center bg-muted/40 rounded-xl p-6 min-h-[140px]'
      : previewVariant === 'hero'
        ? 'rounded-xl overflow-hidden bg-muted/40 aspect-[21/9] max-h-52'
        : 'rounded-xl overflow-hidden bg-muted/40 aspect-video max-h-52';

  const imgClass =
    previewVariant === 'logo'
      ? 'max-h-28 max-w-full w-auto object-contain'
      : 'w-full h-full object-cover';

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    revokeBlob();
    const u = URL.createObjectURL(file);
    blobRef.current = u;
    setLocalPreview(u);

    try {
      setUploading(true);
      const { image_url: nextUrl } = await uploadFile(file);
      if (typeof nextUrl !== 'string' || !nextUrl.trim()) {
        throw new Error('Sin URL');
      }
      onChange(nextUrl.trim());
      toast.success('Imagen subida.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir la imagen.');
    } finally {
      setUploading(false);
      revokeBlob();
      setLocalPreview(null);
    }
  };

  const clearRemote = () => {
    revokeBlob();
    setLocalPreview(null);
    onChange(null);
    toast.message('Campo vaciado. Pulsa Guardar para aplicar.');
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor={inputId} className="text-base font-medium">
          {label}
        </Label>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
            className="hidden"
            tabIndex={-1}
            onChange={onPickFile}
            disabled={uploading}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Subiendo…' : 'Subir archivo'}
          </Button>
          {displaySrc ? (
            <Button type="button" variant="ghost" size="sm" className="gap-2 text-destructive" onClick={clearRemote}>
              <X className="w-4 h-4" />
              Quitar
            </Button>
          ) : null}
        </div>
      </div>

      <div className={`border border-border ${previewWrap}`}>
        {uploading ? (
          <div className="flex h-full min-h-[120px] w-full items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-sm">Subiendo y generando vista previa…</span>
          </div>
        ) : displaySrc ? (
          <img src={displaySrc} alt="Vista previa" className={imgClass} />
        ) : (
          <div className="flex h-full min-h-[120px] w-full flex-col items-center justify-center gap-2 text-muted-foreground p-6">
            <ImageIcon className="w-10 h-10 opacity-60" />
            <span className="text-sm text-center">Sin imagen · sube una o pega una URL abajo</span>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor={inputId} className="text-xs text-muted-foreground font-normal">
          URL alternativa (opcional)
        </Label>
        <Input
          id={inputId}
          type="text"
          value={url || ''}
          onChange={(e) => {
            const v = e.target.value.trim();
            onChange(v !== '' ? v : null);
          }}
          placeholder="https://… o ruta después de subir"
        />
      </div>
    </div>
  );
}
